import type { ReactNode } from 'react';

// Phase 6B compose field primitives — small labeled inputs reused across the
// intro/outro editors (B2) and the per-type question editors (B4). Kept local
// to the surveys module; intentionally plain (no slug picker — NEW-3: intro /
// outro / question text are plain markdown/text, slugs are invite-only).

const inputClass =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500';

export function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
      {children}
    </label>
  );
}

export function LabeledInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={inputClass}
      />
    </div>
  );
}

export function LabeledNumber({
  id,
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
}: {
  id?: string;
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="number"
        value={value ?? ''}
        onChange={(e) => {
          const t = e.target.value.trim();
          onChange(t === '' ? null : Number(t));
        }}
        placeholder={placeholder}
        min={min}
        max={max}
        className={inputClass}
      />
    </div>
  );
}

// Plain markdown textarea (NEW-3: no slug picker on survey content). The
// markdown → HTML render happens on the public survey page (Phase D).
export function MarkdownField({
  id,
  label,
  value,
  onChange,
  rows = 6,
  placeholder,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
      />
      <p className="mt-1 text-xs text-slate-400">Markdown supported.</p>
    </div>
  );
}

// A bordered section card wrapping a labeled group of fields.
export function EditorCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
